<template>
  <div @submit.prevent>
    <!-- ══ TÉRMINOS STRIPE ══ -->
    <div v-if="props.mostrarTerminosStripe">
      <TerminosYCondicionesStripe />
      <div class="text-end mt-3">
        <button
          class="btn-nav btn-secondary-nav"
          @click="$emit('toggle-terminos-stripe', false)"
        >
          <i class="bi bi-arrow-left"></i> Volver al formulario de pago
        </button>
      </div>
    </div>

    <!-- ══ FORMULARIO DE PAGO ══ -->
    <div v-else>
      <!-- Header Stripe -->
      <div class="stripe-header">
        <img
          src="https://cdn.worldvectorlogo.com/logos/stripe-4.svg"
          alt="Stripe"
          width="60"
        />
        <span class="stripe-desc">
          Pagos 100% seguros gestionados por <strong>Stripe</strong>
          <i class="bi bi-shield-lock-fill text-success ms-1"></i>
        </span>
      </div>

      <!-- Badge resumen de compra -->
      <div class="precio-badge">
        <span class="precio-label">Total a pagar</span>
        <span class="precio-monto">{{ formatPrecio(totalCompra) }}</span>
        <span class="precio-desc">
          {{ store.boleto.nombreBoleto }} · {{ store.boleto.cantidad }}
          {{ store.boleto.cantidad === 1 ? "boleto" : "boletos" }}
        </span>
      </div>

      <h6 class="card-titulo">Datos de tu tarjeta</h6>

      <!-- Stripe Elements -->
      <div class="stripe-fields">
        <div class="stripe-field-wrap">
          <div id="card-element" class="stripe-element"></div>
        </div>
      </div>
      <div id="card-errors" class="card-errors-msg"></div>

      <p class="stripe-nota">
        ¿Dudas sobre cómo Stripe protege tu información?
        <a href="#" @click.prevent="$emit('toggle-terminos-stripe', true)">Conoce más</a>
      </p>

      <!-- Navegación -->
      <div class="nav-row">
        <button class="btn-nav btn-secondary-nav" @click="handlePrevStep">
          <i class="bi bi-arrow-left"></i>
          <span class="btn-text">Atrás</span>
        </button>
        <a href="/" class="btn-nav btn-ghost-nav">
          <i class="bi bi-house-fill"></i>
          <span class="btn-text">Inicio</span>
        </a>
        <button
          class="btn-nav btn-primary-nav"
          type="button"
          :disabled="!stripeLoaded"
          @click="mostrarResumen = true"
        >
          <span class="btn-text">Ver resumen</span>
          <i class="bi bi-eye"></i>
        </button>
      </div>

      <!-- ══ Modal Resumen ══ -->
      <transition name="fade">
        <div
          v-if="mostrarResumen"
          class="resumen-overlay"
          @click.self="mostrarResumen = false"
        >
          <div class="resumen-box">
            <h4 class="resumen-titulo">Resumen de compra</h4>

            <!-- Comprador -->
            <div class="resumen-seccion">
              <p class="resumen-row">
                <span><i class="bi bi-person"></i> Nombre</span>
                <strong
                  >{{ store.comprador.nombre }} {{ store.comprador.apellido }}</strong
                >
              </p>
              <p class="resumen-row">
                <span><i class="bi bi-envelope"></i> Correo</span>
                <strong>{{ store.comprador.email }}</strong>
              </p>
              <p class="resumen-row">
                <span><i class="bi bi-phone"></i> Teléfono</span>
                <strong>{{ store.comprador.telefono }}</strong>
              </p>
            </div>

            <!-- Boleto -->
            <div class="resumen-seccion">
              <p class="resumen-row">
                <span><i class="bi bi-ticket-perforated"></i> Tipo de boleto</span>
                <strong>{{ store.boleto.nombreBoleto }}</strong>
              </p>
              <p class="resumen-row">
                <span><i class="bi bi-hash"></i> Cantidad</span>
                <strong>{{ store.boleto.cantidad }}</strong>
              </p>
              <p class="resumen-row">
                <span><i class="bi bi-person-check"></i> Asistiré al evento</span>
                <strong>{{ store.comprador.esAsistente ? "Sí" : "No" }}</strong>
              </p>
              <p v-if="store.boleto.peleadorNombre" class="resumen-row">
                <span><i class="bi bi-person-badge"></i> Peleador favorito</span>
                <strong>{{ store.boleto.peleadorNombre }}</strong>
              </p>
            </div>

            <div class="resumen-total">
              <span>Total</span>
              <strong>{{ formatPrecio(totalCompra) }}</strong>
            </div>

            <div class="resumen-nav">
              <button
                class="btn-nav btn-secondary-nav"
                @click.stop="mostrarResumen = false"
              >
                Cancelar
              </button>
              <button
                class="btn-nav btn-success-nav"
                :disabled="isProcessing"
                @click="confirmarCompra"
              >
                <span v-if="isProcessing">
                  <span class="spinner-sm"></span> Procesando...
                </span>
                <span v-else>
                  <i class="bi bi-check-circle-fill"></i> Confirmar compra
                </span>
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";
import { loadStripe } from "@stripe/stripe-js";

import TerminosYCondicionesStripe from "@/components/Torneo/TerminosYCondicionesStripe.vue";
import { useCompraBoletosStore } from "@/store/CompraBoletosStore";
import { iniciarCompraBoleto } from "@/utils/api_evento";

const store = useCompraBoletosStore();
const router = useRouter();

const props = defineProps({ mostrarTerminosStripe: Boolean });

const isProcessing = ref(false);
const mostrarResumen = ref(false);
const stripeLoaded = ref(false);

// Al volver atrás limpiamos el pago: si el usuario cambia boleto, correo o
// cantidad, el PaymentIntent anterior ya no corresponde a los nuevos datos.
function handlePrevStep() {
  store.clearPago();
  emit("prev-step");
}

const emit = defineEmits(["prev-step", "toggle-terminos-stripe", "confirmar-compra"]);

// Computed local: evita depender del getter rehidratado del store (persist: true)
// puede quedar como string tras recargar; aquí forzamos siempre números.
const totalCompra = computed(
  () =>
    (parseFloat(store.boleto.precioUnitario) || 0) *
    (parseInt(store.boleto.cantidad) || 1)
);

let stripe, elements, cardElement;

const formatPrecio = (v) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
    parseFloat(v) || 0
  );

onMounted(async () => {
  stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY, { locale: "es" });
  elements = stripe.elements();

  const style = {
    base: {
      color: "#32325d",
      fontFamily: "Roboto, sans-serif",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": { color: "#a0aec0" },
    },
    invalid: { color: "#e3342f", iconColor: "#e3342f" },
  };

  cardElement = elements.create("card", { style, hidePostalCode: true });
  cardElement.mount("#card-element");

  cardElement.on("change", ({ error }) => {
    const display = document.getElementById("card-errors");
    if (display) display.textContent = error ? error.message : "";
  });

  stripeLoaded.value = true;
});

// ✅ FIX: Traduce los códigos de error de Stripe a mensajes accionables en español
function traducirErrorStripe(error) {
  const mapa = {
    card_declined: {
      titulo: "Tarjeta declinada",
      html:
        "Tu banco rechazó el pago. Verifica que tu tarjeta tenga fondos suficientes o <b>intenta con otra tarjeta</b>.",
    },
    insufficient_funds: {
      titulo: "Fondos insuficientes",
      html: "Tu tarjeta no tiene saldo suficiente para completar esta compra.",
    },
    expired_card: {
      titulo: "Tarjeta vencida",
      html: "La tarjeta que ingresaste está vencida. Por favor usa una tarjeta vigente.",
    },
    incorrect_cvc: {
      titulo: "CVC incorrecto",
      html:
        "El código de seguridad (CVC/CVV) que ingresaste no es correcto. Revisa el número de 3 dígitos al reverso de tu tarjeta.",
    },
    incorrect_number: {
      titulo: "Número de tarjeta incorrecto",
      html: "El número de tarjeta no es válido. Revísalo e intenta de nuevo.",
    },
    processing_error: {
      titulo: "Error de procesamiento",
      html:
        "Ocurrió un error al procesar tu pago. Espera unos segundos e <b>intenta de nuevo</b>.",
    },
    do_not_honor: {
      titulo: "Pago no autorizado",
      html:
        "Tu banco no autorizó el pago. Contacta a tu banco o intenta con otra tarjeta.",
    },
    authentication_required: {
      titulo: "Autenticación requerida",
      html:
        "Tu banco requiere verificación adicional (3D Secure). Revisa tu app bancaria o SMS.",
    },
    // Ocurre cuando el client_secret quedó guardado en el store (persist:true)
    // pero el PaymentIntent ya fue cancelado en Stripe (por webhook o por expiración).
    // Limpiamos el store para que el siguiente intento cree uno nuevo desde cero.
    payment_intent_unexpected_state: {
      titulo: "Sesión de pago expirada",
      html:
        "Tu sesión de pago anterior expiró. Presiona <b>Entendido</b> para iniciar un nuevo intento — no necesitas volver a llenar tus datos.",
      clearPago: true, // señal para que confirmarCompra limpie el store tras mostrar el error
    },
  };

  // Intenta con el decline_code primero, luego con el code
  return (
    mapa[error.decline_code] ||
    mapa[error.code] || {
      titulo: "Pago rechazado",
      html: error.message || "No pudimos completar el pago. Intenta de nuevo.",
    }
  );
}

async function confirmarCompra() {
  if (isProcessing.value) return;

  // 1. Validar que la tarjeta esté completa
  if (!cardElement._complete) {
    Swal.fire({
      icon: "warning",
      title: "Tarjeta incompleta",
      text: "Completa los datos de tu tarjeta antes de confirmar.",
      confirmButtonColor: "#007bff",
    });
    return;
  }

  isProcessing.value = true;

  try {
    // 2. Obtener el client_secret
    //
    // ✅ FIX REINTENTO: Si ya existe un client_secret en el store (de un intento
    // anterior fallido por tarjeta declinada), lo REUTILIZAMOS directamente.
    // Stripe permite reintentar un PaymentIntent en estado 'requires_payment_method'
    // con un nuevo método de pago usando el mismo client_secret.
    // Llamar de nuevo al backend generaría un 400 porque el Comprador/CompraBoleto
    // ya existe en estado 'pendiente' para ese intento.
    let clientSecret = store.pago.clientSecret;

    if (!clientSecret) {
      // Primera vez — crear la compra en el backend y obtener el PaymentIntent
      const payload = {
        tipo_boleto: store.boleto.idBoleto,
        cantidad: store.boleto.cantidad,
        terminos_aceptados: store.aceptaTerminos,
        // ✅ FIX: el Step2 ya obliga a elegir peleador y lo guarda en el
        // store, pero nunca se incluía en el payload real que se manda al
        // backend — se perdía en el camino. El backend ahora lo requiere
        // (CompraBoleto.peleador_elegido).
        peleador_elegido: store.boleto.peleadorId,
        comprador: {
          nombre: store.comprador.nombre,
          apellido: store.comprador.apellido,
          email: store.comprador.email,
          fecha_nacimiento: store.comprador.fechaNacimiento,
          telefono: store.comprador.telefono,
          es_asistente: store.comprador.esAsistente,
        },
      };

      const respuesta = await iniciarCompraBoleto(payload);
      store.pago.compraId = respuesta.compra_id;
      store.pago.clientSecret = respuesta.client_secret;
      clientSecret = respuesta.client_secret;
    }
    // Si clientSecret ya existía → es un reintento con otra tarjeta, lo usamos tal cual.

    // 3. Confirmar el pago con Stripe
    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: `${store.comprador.nombre} ${store.comprador.apellido}`,
          email: store.comprador.email,
          phone: store.comprador.telefono,
        },
      },
    });

    if (error) {
      // Incrementar contador de fallos para este PaymentIntent
      store.pago.intentosFallidos = (store.pago.intentosFallidos || 0) + 1;

      const mensajeStripe = traducirErrorStripe(error);

      // Casos que requieren limpiar el store:
      // 1. El error mismo lo indica (payment_intent_unexpected_state → intent cancelado)
      // 2. Demasiados fallos consecutivos (≥3) con el mismo PaymentIntent
      const demasiadosFallos = store.pago.intentosFallidos >= 3;
      const debeReiniciar = mensajeStripe.clearPago || demasiadosFallos;
      if (debeReiniciar) store.clearPago();

      Swal.fire({
        icon: "error",
        title: mensajeStripe.titulo,
        html:
          mensajeStripe.html +
          (demasiadosFallos && !mensajeStripe.clearPago
            ? "<br><br><small style='color:#888'>Se ha restablecido el proceso. Inténtalo de nuevo.</small>"
            : ""),
        confirmButtonColor: "#007bff",
        confirmButtonText: "Entendido",
      });
      return;
    }

    // 4. Pago exitoso — limpiar contador de fallos y guardar ID del intento
    store.pago.intentosFallidos = 0;
    store.pago.paymentIntentId = paymentIntent.id;
    mostrarResumen.value = false;

    await Swal.fire({
      icon: "success",
      title: "¡Compra exitosa!",
      html: `
        <p>Hemos recibido tu pago correctamente.</p>
        <p>En breve recibirás un correo con tus boletos y los links para que tus invitados se registren.</p>
      `,
      confirmButtonText: "Entendido",
      confirmButtonColor: "#007bff",
    });

    store.resetFormulario();
    router.push("/");
  } catch (err) {
    console.error("Error en la compra:", err);

    const backendMsg =
      err?.response?.data?.detail ||
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      (typeof err?.response?.data === "string" ? err.response.data : null);

    const statusCode = err?.response?.status;

    let titulo = "Error al procesar";
    let html = "Ocurrió un error inesperado. Intenta de nuevo o contáctanos.";

    if (statusCode === 400 && backendMsg) {
      titulo = "Datos inválidos";
      html = backendMsg;
    } else if (statusCode === 409) {
      titulo = "Compra duplicada";
      html =
        "Ya existe una compra activa con este correo. Si tuviste un pago fallido anteriormente, <b>puedes intentar de nuevo</b> con la misma dirección de correo.";
    } else if (statusCode === 402) {
      titulo = "Pago rechazado";
      html =
        backendMsg ||
        "Tu banco rechazó el pago. Revisa los datos de tu tarjeta o intenta con otra.";
    } else if (statusCode >= 500) {
      titulo = "Error del servidor";
      html =
        "Hubo un problema en nuestros servidores. Por favor intenta en unos minutos.";
    } else if (backendMsg) {
      html = backendMsg;
    }

    // Si el error viene del backend al crear la compra, limpiamos el pago
    // para que el siguiente intento no quede con un estado corrupto.
    if (statusCode && statusCode !== 402) {
      store.clearPago();
    }

    Swal.fire({
      icon: "error",
      title: titulo,
      html,
      confirmButtonColor: "#007bff",
      confirmButtonText: "Entendido",
      footer: statusCode
        ? `<small style="color:#aaa">Estado HTTP: ${statusCode}</small>`
        : undefined,
    });
  } finally {
    isProcessing.value = false;
  }
}
</script>

<style scoped>
/* ── Stripe header ── */
.stripe-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}
.stripe-desc {
  font-size: 0.82rem;
  color: #666;
}

/* ── Badge precio ── */
.precio-badge {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: #f0f7ff;
  border: 1px solid #c8e0ff;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 20px;
}
.precio-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.precio-monto {
  font-size: 1.5rem;
  font-weight: 800;
  color: #007bff;
}
.precio-desc {
  font-size: 0.78rem;
  color: #888;
  margin-left: auto;
}

/* ── Stripe fields ── */
.card-titulo {
  font-weight: 700;
  color: #333;
  margin-bottom: 14px;
  font-size: 0.95rem;
}
.stripe-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 6px;
}
.stripe-field-wrap {
  border: 1.5px solid #d8d8d8;
  border-radius: 8px;
  padding: 10px 12px;
  transition: border-color 0.2s;
}
.stripe-field-wrap:focus-within {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}
.stripe-element {
  min-height: 22px;
}
.card-errors-msg {
  color: #dc3545;
  font-size: 0.8rem;
  min-height: 20px;
  margin-bottom: 4px;
}
.stripe-nota {
  font-size: 0.78rem;
  color: #999;
  text-align: right;
  margin-top: 10px;
}
.stripe-nota a {
  color: #007bff;
}

/* ── Navegación ── */
.nav-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
.btn-nav {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  text-decoration: none;
  transition: all 0.2s ease;
}
.btn-primary-nav {
  background: #007bff;
  color: #fff;
}
.btn-primary-nav:hover {
  background: #0060df;
}
.btn-primary-nav:disabled {
  background: #b0c8f0;
  cursor: not-allowed;
}
.btn-secondary-nav {
  background: #f0f0f0;
  color: #555;
}
.btn-secondary-nav:hover {
  background: #e0e0e0;
}
.btn-ghost-nav {
  background: transparent;
  color: #888;
  border: 1.5px solid #ddd;
}
.btn-ghost-nav:hover {
  border-color: #bbb;
  color: #555;
}
.btn-success-nav {
  background: linear-gradient(135deg, #28a745, #1e8035);
  color: #fff;
  font-weight: 700;
}
.btn-success-nav:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(40, 167, 69, 0.3);
}
.btn-success-nav:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Spinner ── */
.spinner-sm {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  animation: giro 0.8s linear infinite;
  vertical-align: middle;
}
@keyframes giro {
  to {
    transform: rotate(360deg);
  }
}

/* ── Modal resumen ── */
.resumen-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.52);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.resumen-box {
  background: linear-gradient(180deg, #ffffff, #f8fafc);
  border-radius: 20px;
  padding: 2.2rem;
  max-width: 480px;
  width: 92%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(0, 170, 255, 0.08);
}
.resumen-titulo {
  font-weight: 900;
  font-size: 1.2rem;
  color: #0f172a;
  margin-bottom: 1.5rem;
  padding-bottom: 14px;
  border-bottom: 2px solid transparent;
  background: linear-gradient(90deg, #00aaff, #007bff) bottom;
  background-size: 100% 2px;
  background-repeat: no-repeat;
  letter-spacing: 1px;
  text-transform: uppercase;
  text-align: center;
}
.resumen-seccion {
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e2e8f0;
}
.resumen-seccion:last-of-type {
  border-bottom: none;
}
.resumen-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin: 0 0 8px;
  font-size: 0.88rem;
}
.resumen-row span {
  color: #64748b;
  font-weight: 500;
}
.resumen-row strong {
  color: #0f172a;
  font-weight: 700;
}
.resumen-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #00aaff, #007bff);
  color: white;
  border-radius: 12px;
  padding: 8px 18px;
  margin: 10px 0;
}
.resumen-total span {
  font-size: 0.9rem;
  font-weight: 600;
  opacity: 0.85;
}
.resumen-total strong {
  font-size: 1.6rem;
  font-weight: 900;
  color: #fff;
}
.resumen-nav {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 20px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 600px) {
  .precio-desc {
    margin-left: 0;
  }
}
@media (max-width: 375px) {
  .btn-text {
    display: none;
  }
  .btn-nav {
    justify-content: center;
    padding: 9px 14px;
  }
}
</style>
